import type { ErrorInfo } from "react";
import { hydrateRoot } from "react-dom/client";
import type { FormName } from "@searchui-searchforms/shared/config";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { invariant } from "@searchui-searchforms/shared/utils";
import { RenderEntry } from "@/app/lib/render-entries";
import { createI18nInstance } from "@/shared/i18n";
import { EN_LANGUAGE } from "@/shared/i18n/en-language";
import { logFCI } from "@/shared/lib/fci";
import { makeQueryClient } from "@/shared/queryClient";
import type { ClassicFormProps } from "@/pages/classic-form";
import type { SliderFormProps } from "@/pages/slider-form";
import { SFS_FORM_NAME, SFS_HYDRATION_STATE, SFS_RENDER_ROOT } from "./constants";
import type { HydrationState } from "./constants";

type ComponentProps = ClassicFormProps | SliderFormProps;

const setHydrationState = (el: HTMLElement, state: HydrationState) => {
    el.setAttribute(SFS_HYDRATION_STATE, state);
};

export const registerAttach = (Component: React.ComponentType<ComponentProps>, formName: FormName) => {
    const cleanup = new Map<string, () => void>();

    const attach = async (id: string) => {
        try {
            const rootEl = document.getElementById(id);
            invariant(rootEl, "[webpart] No root element found for id");

            // Hydration state guard
            const currentState = rootEl.getAttribute(SFS_HYDRATION_STATE) as HydrationState | null;
            if (currentState === "pending" || currentState === "hydrated") {
                console.warn(`[webpart] Skipping attach for "${id}" — already ${currentState}`);
                return;
            }

            // Form name validation
            const elementFormName = rootEl.getAttribute(SFS_FORM_NAME);
            invariant(
                elementFormName === formName,
                `[webpart] Form name mismatch: expected "${formName}", got "${elementFormName}"`,
            );

            // Set pending
            setHydrationState(rootEl, "pending");

            const props = rootEl.dataset.ssrProps;
            invariant(typeof props === "string", "[webpart] No props found at root element");

            const parsedProps = JSON.parse(props);
            const language = rootEl.dataset.i18nLang || EN_LANGUAGE;
            const i18nInstance = await createI18nInstance({ language });
            const queryClient = makeQueryClient();

            const handleRecoverableError = (error: unknown, errorInfo: ErrorInfo) => {
                logFCI({
                    errorType: FCI_ERROR.TransparentSubstitution,
                    errorInfo: `Recoverable webpart render error: ${error}`,
                    details: {
                        stack: error instanceof Error ? error.stack : undefined,
                        componentStack: errorInfo.componentStack ?? undefined,
                        formName,
                    },
                });
            };

            const reactDOMRoot = hydrateRoot(
                rootEl,
                <RenderEntry
                    Component={Component}
                    componentProps={{ ...parsedProps, i18n: i18nInstance, queryClient }}
                    formName={formName}
                />,
                { onRecoverableError: handleRecoverableError },
            );

            setHydrationState(rootEl, "hydrated");
            cleanup.set(id, () => reactDOMRoot.unmount());
        } catch (error) {
            // Set error state on the element if it exists
            const rootEl = document.getElementById(id);
            if (rootEl) {
                setHydrationState(rootEl, "error");
            }
            logFCI({
                errorType: FCI_ERROR.Main,
                errorInfo: `webpart attach() failed: ${error}`,
                details: {
                    stack: error instanceof Error ? error.stack : undefined,
                    formName,
                },
            });
            throw error;
        }
    };

    const unmount = (id: string) => {
        const cleanupFunc = cleanup.get(id);
        if (cleanupFunc) {
            cleanupFunc();
            cleanup.delete(id);
        }
    };

    // Process queued attach calls from inline scripts that ran before this bundle loaded
    const webpartApi = window.ancestry?.search?.forms?.api?.webpart;
    if (webpartApi) {
        const attachFn = webpartApi[formName]?.attach;
        const queue = attachFn?.queue;
        if (queue) {
            queue.forEach((id: string) => attach(id));
            queue.clear();
        }
    }

    // Fallback DOM discovery: hydrate elements that were injected via .innerHTML
    // (where the inline script never ran and queue was never populated)
    const unhydrated = document.querySelectorAll<HTMLElement>(
        `[${SFS_HYDRATION_STATE}="none"][${SFS_RENDER_ROOT}][${SFS_FORM_NAME}="${formName}"]`,
    );
    unhydrated.forEach((el) => {
        if (el.id) attach(el.id);
    });

    // Register the global API
    // Make sure that this is the last step, otherwise the list
    // Of SSR-rendered forms would become unavailable (webpartApi[formName]?.attach)
    if (!window.ancestry) window.ancestry = {};
    if (!window.ancestry.search) window.ancestry.search = {};
    if (!window.ancestry.search.forms) window.ancestry.search.forms = {};
    if (!window.ancestry.search.forms.api) window.ancestry.search.forms.api = {};
    if (!window.ancestry.search.forms.api.webpart) window.ancestry.search.forms.api.webpart = {};
    window.ancestry.search.forms.api.webpart[formName] = { attach, unmount };
};
